import { useMemo } from 'react';
import { OneOfListElements } from '@shared/models/project.model';
import { GroupSectionData } from '@ui/ElementList/list.model';
import {
  useElementMaxResults,
  useGetResultsPerGFA,
  useCo2eChartData,
  useCostChartData,
} from '@features/results/use-results.hooks';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { ISimpleBarChartValue } from '@ui/BarChart';
import { GroupSectionMeta } from '../element-grouping.model';

export type GroupSectionChartData = {
  co2eValues: ISimpleBarChartValue[];
  sekValues: ISimpleBarChartValue[];
  co2eMax: number;
  sekMax: number;
};

export const useGroupSectionChartData = (
  section: GroupSectionData<OneOfListElements, GroupSectionMeta>,
): GroupSectionChartData => {
  const getResultsPerGFA = useGetResultsPerGFA();
  const maxResults = useElementMaxResults();

  const aggregatedPerGFA = useMemo(
    () => getResultsPerGFA(section.meta.resultsSum),
    [section.meta.resultsSum, getResultsPerGFA],
  );

  const co2eValues = useCo2eChartData(aggregatedPerGFA);
  const sekValues = useCostChartData(aggregatedPerGFA);

  const maxResultsPerGFA = useMemo(
    () => getResultsPerGFA(maxResults),
    [getResultsPerGFA, maxResults],
  );

  const co2eMax = getConversionFactorValue(maxResultsPerGFA, 'co2e');
  const sekMax = getConversionFactorValue(maxResultsPerGFA, 'sek');

  return { co2eValues, sekValues, co2eMax, sekMax };
};
